import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple,
  Headphones, Settings, Link as LinkIcon, ArrowUpRight, Sparkles
} from "lucide-react";
import { SERVICES, servicePath, serviceSlug } from "../lib/constants";

const ICON_MAP = {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple,
  Headphones, Settings, Link: LinkIcon,
};

export default function Services() {
  // Bento layout — split services into hero card + small cards + wide card
  const [hero, ...rest] = SERVICES;
  const small = rest.slice(0, 5);
  const wide = rest[5];
  const tail = rest.slice(6);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative theme-section py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />
      <div className="absolute -top-40 right-1/4 w-[500px] h-[500px] bg-[#00C2FF] rounded-full blur-[220px] opacity-15" />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header */}
        <div className="grid grid-cols-12 items-end gap-8 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-span-12 md:col-span-8"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-[#00C2FF]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
                / Services
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter theme-heading">
              Digital delivery for
              <br />
              <span className="text-gradient-cyan italic">serious trading desks.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="col-span-12 md:col-span-4 text-gray-400 text-sm leading-relaxed"
          >
            Each offering is delivered by the engineers behind our core platform — consistent
            performance, security controls, and scaling practices across every project.
          </motion.p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-5">
          {/* Hero card — 5 cols / 2 rows */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-span-12 md:col-span-5 row-span-2"
          >
          <Link
            to={servicePath(serviceSlug(hero.title))}
            data-testid="service-card-hero"
            className="group relative overflow-hidden rounded-3xl border border-[#00C2FF]/30 bg-gradient-to-br from-[#001a2e] via-[#000a14] to-black p-8 sm:p-10 min-h-[420px] flex flex-col justify-between h-full"
          >
            <div className="absolute -top-32 -right-32 w-80 h-80 bg-[#00C2FF] rounded-full blur-[140px] opacity-30 group-hover:opacity-50 transition-opacity" />
            <div className="absolute inset-0 dot-grid opacity-30" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-[#00C2FF]/15 border border-[#00C2FF]/30 text-[#00C2FF] px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.25em] font-bold mb-6">
                <Sparkles className="w-3 h-3" /> Featured
              </div>
              <div className="w-16 h-16 rounded-2xl bg-[#00C2FF]/15 border border-[#00C2FF]/40 flex items-center justify-center mb-7">
                {React.createElement(ICON_MAP[hero.icon] || FileText, { className: "w-8 h-8 text-[#00C2FF]" })}
              </div>
              <h3 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight">
                {hero.title}
              </h3>
              <p className="text-gray-400 mt-4 leading-relaxed max-w-md">{hero.desc}</p>
            </div>

            <div className="relative flex items-center justify-between mt-8">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#00C2FF]">
                Learn more
              </span>
              <div className="w-12 h-12 rounded-full border border-[#00C2FF]/40 flex items-center justify-center group-hover:bg-[#00C2FF] group-hover:text-black transition-all">
                <ArrowUpRight className="w-5 h-5" />
              </div>
            </div>
          </Link>
          </motion.div>

          {/* Small cards — 7 cols, varying widths */}
          {small.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || FileText;
            // Layout: 2 rows of varied widths inside 7-col area
            // row 1: 4 + 3
            // row 2: 3 + 4
            const widths = ["md:col-span-4", "md:col-span-3", "md:col-span-3", "md:col-span-4", "md:col-span-7"];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                className={`col-span-12 sm:col-span-6 ${widths[i]}`}
              >
              <Link
                to={servicePath(serviceSlug(s.title))}
                data-testid={`service-card-${i}`}
                className="group relative overflow-hidden rounded-2xl theme-panel border hover:border-[#00C2FF]/50 p-6 transition-all hover:-translate-y-1 min-h-[180px] block h-full"
              >
                <div className="absolute -top-16 -right-16 w-32 h-32 bg-[#00C2FF] rounded-full blur-3xl opacity-0 group-hover:opacity-25 transition-opacity duration-500" />
                <div className="relative flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#00C2FF]/10 border border-[#00C2FF]/30 flex items-center justify-center text-[#00C2FF]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="font-mono text-[10px] text-gray-600 tracking-widest">
                    {String(i + 2).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="relative font-display text-lg font-semibold mt-5 leading-snug">{s.title}</h3>
                <p className="relative text-xs text-gray-400 mt-2 leading-relaxed line-clamp-2">{s.desc}</p>
                <ArrowUpRight className="absolute bottom-5 right-5 w-4 h-4 text-gray-600 group-hover:text-[#00C2FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </Link>
              </motion.div>
            );
          })}

          {/* Wide card */}
          {wide && (() => {
            const Icon = ICON_MAP[wide.icon] || FileText;
            return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.35, duration: 0.55 }}
                className="col-span-12 md:col-span-7"
              >
              <Link
                to={servicePath(serviceSlug(wide.title))}
                data-testid="service-card-wide"
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-r from-[#050B14] to-[#001a2e] hover:border-[#00C2FF]/50 p-7 transition-all hover:-translate-y-1 min-h-[150px] flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6"
              >
                <div className="w-14 h-14 shrink-0 rounded-xl bg-[#9FE3FF]/10 border border-[#9FE3FF]/30 flex items-center justify-center text-[#9FE3FF]">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-semibold">{wide.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{wide.desc}</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-gray-600 group-hover:text-[#00C2FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </Link>
              </motion.div>
            );
          })()}

          {/* Tail cards */}
          {tail.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || FileText;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + i * 0.07, duration: 0.55 }}
                className="col-span-12 sm:col-span-6 md:col-span-6"
              >
              <Link
                to={servicePath(serviceSlug(s.title))}
                data-testid={`service-card-tail-${i}`}
                className="group relative overflow-hidden rounded-2xl theme-panel border hover:border-[#00C2FF]/50 p-6 transition-all hover:-translate-y-1 block h-full"
              >
                <div className="absolute -top-16 -right-16 w-32 h-32 bg-[#00C2FF] rounded-full blur-3xl opacity-0 group-hover:opacity-25 transition-opacity" />
                <div className="relative flex items-start gap-5">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-[#00C2FF]/10 border border-[#00C2FF]/30 flex items-center justify-center text-[#00C2FF]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold leading-snug">{s.title}</h3>
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
                <ArrowUpRight className="absolute bottom-5 right-5 w-4 h-4 text-gray-600 group-hover:text-[#00C2FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
